import React from "react";
import { Link, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X, User, LogOut } from "lucide-react";
import { useCart } from "../lib/cart";
import { useAuth } from "../lib/auth";
import BrandMark from "./BrandMark";

export default function Header() {
  const { items } = useCart();
  const { user, logout } = useAuth();
  const [open, setOpen] = React.useState(false);
  const [accountOpen, setAccountOpen] = React.useState(false);
  const loc = useLocation();

  const links = [
    { to: "/music", label: "Music" },
    { to: "/photography", label: "Photography" },
    { to: "/drawings", label: "Artwork" },
    { to: "/about", label: "About" },
    { to: "/contact", label: "Contact" },
  ];

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 left-0 right-0 z-50 bg-black/60 backdrop-blur-xl border-b border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-5 flex items-center justify-between gap-6">
        <Link to="/" data-testid="header-logo-link" className="flex items-center gap-3 group flex-shrink-0">
          <BrandMark variant="knot" className="h-11 w-11" />
          <BrandMark variant="wordmark" className="h-11 w-auto max-w-[110px]" />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={`text-xs tracking-[0.25em] uppercase transition-colors ${
                loc.pathname === l.to ? "text-white" : "text-[#95A89C] hover:text-white"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          {/* Account */}
          {user ? (
            <div className="relative">
              <button
                data-testid="account-menu-toggle"
                onClick={() => setAccountOpen((o) => !o)}
                className="flex items-center gap-2 px-3 py-2 border border-white/15 text-xs tracking-[0.2em] uppercase hover:border-[#279153] hover:text-[#279153] transition-colors"
              >
                <User className="w-4 h-4" />
                <span className="hidden md:inline max-w-[100px] truncate">{user.name || user.email}</span>
              </button>
              {accountOpen && (
                <div className="absolute right-0 top-full mt-2 w-48 bg-[#09120C] border border-[#1A2B20] shadow-xl">
                  <Link
                    to="/orders"
                    onClick={() => setAccountOpen(false)}
                    data-testid="menu-orders"
                    className="block px-4 py-3 text-xs tracking-[0.2em] uppercase text-white hover:bg-[#1A2B20]"
                  >
                    My orders
                  </Link>
                  <button
                    data-testid="menu-logout"
                    onClick={() => { setAccountOpen(false); logout(); }}
                    className="w-full text-left flex items-center gap-2 px-4 py-3 text-xs tracking-[0.2em] uppercase text-white hover:bg-[#1A2B20] border-t border-[#1A2B20]"
                  >
                    <LogOut className="w-3 h-3" /> Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link
              to="/login"
              data-testid="header-login-link"
              className="hidden sm:inline-flex items-center gap-2 px-3 py-2 border border-white/15 text-xs tracking-[0.25em] uppercase hover:border-[#279153] hover:text-[#279153] transition-colors"
            >
              <User className="w-4 h-4" /> Login
            </Link>
          )}
          <Link
            to="/cart"
            data-testid="header-cart-link"
            className="relative flex items-center gap-2 px-3 py-2 border border-white/15 text-xs tracking-[0.25em] uppercase hover:border-[#279153] hover:text-[#279153] transition-colors"
          >
            <ShoppingBag className="w-4 h-4" />
            <span data-testid="cart-count" className="text-[#279153] font-bold">{items.length}</span>
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 text-white"
            onClick={() => setOpen((o) => !o)}
            aria-label="Menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-white/10 bg-black/90 backdrop-blur-xl">
          <div className="px-6 py-6 flex flex-col gap-5">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                className="text-sm tracking-[0.25em] uppercase text-white/80 hover:text-white"
              >
                {l.label}
              </Link>
            ))}
            {!user && (
              <Link to="/login" onClick={() => setOpen(false)} className="text-sm tracking-[0.25em] uppercase text-[#279153]">
                Login / Register
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
